import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import {
  LuBuilding2,
  LuUsers,
  LuTarget,
  LuUserCheck,
  LuSettings,
  LuLayoutDashboard,
  LuTrendingUp, // Switched to LuTrendingUp which is a standard icon
} from 'react-icons/lu';
import SafeIcon from '../../common/SafeIcon';
import { useAuth } from '../../hooks/useAuth';

const Sidebar = ({ isOpen, onClose }) => {
  const { userProfile } = useAuth();
  const location = useLocation();

  // Base navigation items available to all users
  const commonNavigation = [
    { name: 'Dashboard', href: '/', icon: LuLayoutDashboard },
    { name: 'Accounts', href: '/accounts', icon: LuBuilding2 },
    { name: 'Contacts', href: '/contacts', icon: LuUserCheck },
    { name: 'Opportunities', href: '/opportunities', icon: LuTarget },
    { name: 'Settings', href: '/settings', icon: LuSettings },
  ];

  // Admin-only navigation items
  const adminNavigation = [
    { name: 'Executive Dashboard', href: '/executive-dashboard', icon: LuTrendingUp },
    { name: 'Team', href: '/teams', icon: LuUsers },
  ];

  // Construct navigation based on role
  const navigation =
    userProfile?.role === 'Admin'
      ? [
        commonNavigation[0], // Dashboard
        ...adminNavigation,   // Executive Dashboard & Team (Admin only)
        ...commonNavigation.slice(1), // Rest of common items
      ]
      : commonNavigation;

  return (
    <>
      {/* Mobile Overlay */}
      {isOpen && (
        <div
          className="md:hidden z-40 fixed inset-0 bg-gray-600 bg-opacity-75 transition-opacity"
          onClick={onClose}
        ></div>
      )}

      {/* Sidebar Component */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-64 flex flex-col bg-gray-900 transition-transform duration-300 ease-in-out transform
        md:translate-x-0 md:fixed md:inset-y-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        <div className="flex flex-col flex-1 bg-gray-900 min-h-0">
          <div className="flex flex-shrink-0 justify-between items-center bg-gray-900 px-4 h-16">
            <div className="flex items-center">
              <SafeIcon icon={LuBuilding2} className="w-8 h-8 text-white" />
              <span className="ml-3 font-semibold text-white text-lg">
                Provision4 CRM
              </span>
            </div>
            {/* Close button for mobile */}
            <button
              onClick={onClose}
              className="md:hidden focus:outline-none text-gray-400 hover:text-white"
            >
              <span className="sr-only">Close sidebar</span>
              <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          <div className="flex flex-col flex-1 overflow-y-auto">
            <nav className="flex-1 space-y-1 px-2 py-4">
              {navigation.map((item) => {
                const isActive = location.pathname === item.href;
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    onClick={() => onClose && window.innerWidth < 768 && onClose()}
                    className={`group flex items-center px-2 py-2 text-sm font-medium rounded-md transition-colors ${isActive
                      ? 'bg-gray-800 text-white'
                      : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                  >
                    <SafeIcon
                      icon={item.icon}
                      className="flex-shrink-0 mr-3 w-5 h-5"
                      aria-hidden="true"
                    />
                    {item.name}
                  </NavLink>
                );
              })}
            </nav>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;